import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Heading,
  VStack,
  HStack,
  Text,
  Field,
  Input,
  Avatar,
  IconButton,
} from '@chakra-ui/react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { toast } from '../utils/toast';

const Settings: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const isMasterAdmin = user?.role === 'masterAdmin' || user?.email === 'admin@admin.com';
  const isAdmin = user?.role === 'admin';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({ title: 'Error', description: 'Please fill in all fields.', status: 'error' });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: 'Error', description: 'New password must be at least 8 characters.', status: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: 'Error', description: 'New password and confirmation do not match.', status: 'error' });
      return;
    }
    setLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast({ title: 'Success', description: 'Password changed successfully.', status: 'success' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Password change failed.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Desktop sidebar */}
      <Box
        display={{ base: 'none', md: 'flex' }}
        position="fixed"
        top={0}
        left={0}
        h="100vh"
        w="260px"
        bg="white"
        borderRightWidth={1}
        borderColor="gray.200"
        boxShadow="sm"
        p={4}
        flexDirection="column"
        zIndex={15}
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset={0}
          opacity={0.06}
          pointerEvents="none"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <img
            src="/NS_Projects.png"
            alt="NS Projects watermark"
            style={{ maxWidth: '260px' }}
          />
        </Box>

        <Box
          position="relative"
          zIndex={1}
          display="flex"
          flexDirection="column"
          h="100%"
        >
          <Heading
            size="lg"
            mb={2}
            bgGradient="to-r"
            gradientFrom="#273f96"
            gradientTo="#1369b3"
            style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
          >
            Project Manager
          </Heading>
          <HStack gap={2} mb={6}>
            <Avatar.Root size="sm">
              <Avatar.Fallback bg="gray.200" color="black">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </Avatar.Fallback>
            </Avatar.Root>
            <Text fontWeight="medium" color="gray.700">
              {user?.name}
            </Text>
          </HStack>
          <VStack align="stretch" gap={3} flex="1">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              borderRadius="lg"
              _hover={{
                transform: 'translateY(-2px)',
                boxShadow: 'md',
              }}
              justifyContent="flex-start"
              width="100%"
            >
              Dashboard
            </Button>
            {(isMasterAdmin || isAdmin) && (
              <Button
                variant="outline"
                onClick={() => navigate('/admin')}
                borderRadius="lg"
                _hover={{
                  transform: 'translateY(-2px)',
                  boxShadow: 'md',
                }}
                justifyContent="flex-start"
                width="100%"
              >
                Admin Panel
              </Button>
            )}
            <Button
              variant="solid"
              colorScheme="purple"
              borderRadius="lg"
              justifyContent="flex-start"
              width="100%"
            >
              Settings
            </Button>
          </VStack>
          <VStack
            align="stretch"
            gap={2}
            pt={3}
            borderTopWidth={1}
            borderColor="gray.100"
          >
            <Button
              variant="outline"
              onClick={logout}
              borderRadius="lg"
              _hover={{
                bg: 'gray.100',
              }}
              justifyContent="flex-start"
              width="100%"
            >
              Logout
            </Button>
          </VStack>
        </Box>
      </Box>

      {/* Header */}
      <Box bg="white" borderBottomWidth={1} borderColor="gray.200" boxShadow="sm">
        <Container maxW="container.xl" py={4}>
          <HStack justifyContent="space-between">
            <HStack gap={3} alignItems="center">
              <IconButton
                aria-label="Back to dashboard"
                variant="ghost"
                borderRadius="full"
                onClick={() => navigate('/dashboard')}
              >
                <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </IconButton>
              <Heading size="xl" color="gray.800">
                Settings
              </Heading>
            </HStack>
          </HStack>
        </Container>
      </Box>

      <Container
        maxW={{ base: 'container.md', md: '100%' }}
        py={8}
        pl={{ base: 0, md: '280px' }}
        pr={{ base: 0, md: 8 }}
      >
        <VStack gap={8} align="stretch">
          <Box bg="white" borderRadius="xl" p={8} boxShadow="sm" borderWidth={1} borderColor="gray.200">
            <Heading size="lg" color="gray.800" mb={6}>Change password</Heading>
            <form onSubmit={handleSubmit}>
              <VStack gap={5} align="stretch">
                <Field.Root>
                  <label htmlFor="settings-current-password">
                    <Text as="span" fontSize="sm" fontWeight="medium" color="gray.700">Current password</Text>
                  </label>
                  <Input
                    id="settings-current-password"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Current password"
                    size="lg"
                    borderRadius="lg"
                    mt={1}
                  />
                </Field.Root>
                <Field.Root>
                  <label htmlFor="settings-new-password">
                    <Text as="span" fontSize="sm" fontWeight="medium" color="gray.700">New password (min. 8 characters)</Text>
                  </label>
                  <Input
                    id="settings-new-password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password"
                    size="lg"
                    borderRadius="lg"
                    minLength={8}
                    mt={1}
                  />
                </Field.Root>
                <Field.Root>
                  <label htmlFor="settings-confirm-password">
                    <Text as="span" fontSize="sm" fontWeight="medium" color="gray.700">Confirm new password</Text>
                  </label>
                  <Input
                    id="settings-confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    size="lg"
                    borderRadius="lg"
                    mt={1}
                  />
                </Field.Root>
                <Button
                  type="submit"
                  size="lg"
                  loading={loading}
                  bgGradient="to-r"
                  gradientFrom="#273f96"
                  gradientTo="#1369b3"
                  color="white"
                  borderRadius="lg"
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }}
                >
                  Save password
                </Button>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Settings;
